/**
 * Hook for fetching calendar availability data
 * Calls /api/calendar-availability-new endpoint for each service type
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { api } from '@core/api/apiClient';
import { mapToDisplayType } from '../utils/availability';
import type { ServiceType } from '../state/types';
import type {
  AvailabilityDisplayType,
  CalendarAvailabilityResponse,
  ServiceCalendarAvailability,
  MergedCalendarDay,
  CalendarDayInfo,
  CalendarDayType,
} from '../types/calendar';

/**
 * Query key factory for calendar availability
 */
export const calendarAvailabilityKeys = {
  all: ['calendarAvailability'] as const,
  byService: (restaurantId: number, partySize: number, serviceType: ServiceType, meitreNetworkMenuId?: number | null) =>
    meitreNetworkMenuId
      ? [...calendarAvailabilityKeys.all, 'marketplace', partySize, serviceType, meitreNetworkMenuId] as const
      : [...calendarAvailabilityKeys.all, restaurantId, partySize, serviceType] as const,
};

/**
 * Fetch calendar availability for a specific service type.
 * When meitreNetworkMenuId is provided, uses the marketplace endpoint instead.
 */
async function fetchCalendarAvailability(
  restaurantId: number,
  partySize: number,
  serviceType: ServiceType,
  meitreNetworkMenuId?: number | null,
): Promise<ServiceCalendarAvailability> {
  const url = meitreNetworkMenuId
    ? `/api/calendar-marketplace/${partySize}/${serviceType}/${meitreNetworkMenuId}`
    : `/api/calendar-availability-new/${restaurantId}/${partySize}/${serviceType}`;

  const response = await api.get<CalendarAvailabilityResponse>(url);

  return {
    ...response,
    serviceType,
  };
}

/**
 * Extract date string (YYYY-MM-DD) from ISO date
 */
function extractDateString(isoDate: string): string {
  return isoDate.split('T')[0];
}

/**
 * Deduplicate calendar entries within a single service response.
 * The API may return two entries for the same date (e.g., one from special events in UTC,
 * one from normal availability in restaurant timezone). When duplicates exist,
 * the special event entry takes priority since it reflects actual availability
 * (e.g., waiting-list means no direct slots available via search-all-hours).
 */
function deduplicateCalendarInfo(calendarInfo: CalendarDayInfo[]): CalendarDayInfo[] {
  const dateMap = new Map<string, CalendarDayInfo>();

  for (const entry of calendarInfo) {
    const dateKey = extractDateString(entry.date);
    const existing = dateMap.get(dateKey);

    if (!existing) {
      dateMap.set(dateKey, entry);
      continue;
    }

    // Special event entries take priority over normal entries
    if (entry.isSpecialDay && !existing.isSpecialDay) {
      dateMap.set(dateKey, entry);
    }
    // If neither or both are special, keep the existing (first) entry
  }

  return Array.from(dateMap.values());
}

/**
 * Get best type from multiple days using precedence:
 * available > request > waiting-list > walk-in > not-available > not-available-closed
 */
function getBestType(currentType: CalendarDayType | undefined, newType: CalendarDayType): CalendarDayType {
  const precedence: CalendarDayType[] = [
    'available',
    'request',
    'waiting-list',
    'walk-in',
    'not-available',
    'not-available-old-day',
    'not-available-closed',
  ];

  if (!currentType) return newType;

  const currentIndex = precedence.indexOf(currentType);
  const newIndex = precedence.indexOf(newType);

  // Lower index = better availability
  return newIndex < currentIndex ? newType : currentType;
}


/**
 * Merge calendar availability from multiple services into a unified structure.
 * Uses OR logic: a day is "available" if ANY service has availability.
 */
function mergeCalendarAvailability(
  calendars: ServiceCalendarAvailability[]
): MergedCalendarDay[] {
  if (calendars.length === 0) return [];

  // Create a map of date -> merged day data
  const dateMap = new Map<string, MergedCalendarDay>();

  for (const calendar of calendars) {
    // Deduplicate entries within a single service response (API bug: duplicate dates
    // from special events in UTC + normal availability in restaurant timezone)
    const dedupedInfo = deduplicateCalendarInfo(calendar.calendarInfo);

    for (const dayInfo of dedupedInfo) {
      const dateKey = extractDateString(dayInfo.date);

      if (!dateMap.has(dateKey)) {
        // Initialize new day
        dateMap.set(dateKey, {
          date: dateKey,
          services: {},
          hasAnyAvailability: false,
          hasPreferredAvailability: false,
          isWaitingListFull: true,
          availableServiceTypes: [],
          type: 'not-available-closed',
          displayType: 'closed',
          isSpecialDay: false,
          highlightSpecialDay: false,
          centerHour: '',
          minimumOrderAmount: null,
        });
      }

      const mergedDay = dateMap.get(dateKey)!;

      // Store service-specific data
      mergedDay.services[calendar.serviceType] = dayInfo;

      // OR logic: use best available type across all services
      mergedDay.type = getBestType(mergedDay.type, dayInfo.type);
      mergedDay.displayType = mapToDisplayType(mergedDay.type);

      // OR logic for special days
      mergedDay.isSpecialDay = mergedDay.isSpecialDay || dayInfo.isSpecialDay;
      mergedDay.highlightSpecialDay = mergedDay.highlightSpecialDay || dayInfo.highlightSpecialDay;

      // Use first non-empty centerHour and minimumOrderAmount
      if (!mergedDay.centerHour && dayInfo.centerHour) {
        mergedDay.centerHour = dayInfo.centerHour;
      }
      if (mergedDay.minimumOrderAmount === null && dayInfo.minimumOrderAmount !== null) {
        mergedDay.minimumOrderAmount = dayInfo.minimumOrderAmount;
      }

      // Check if this service is available (not closed)
      if (dayInfo.type !== 'not-available-closed') {
        if (!mergedDay.availableServiceTypes.includes(calendar.serviceType)) {
          mergedDay.availableServiceTypes.push(calendar.serviceType);
        }
        if (dayInfo.isAvailable === '1') {
          mergedDay.hasAnyAvailability = true;
        }
        // Track preferred-only availability
        if (dayInfo.isAvailablePreferred === '1') {
          mergedDay.hasPreferredAvailability = true;
        }
        // Waiting list is full only if ALL services with availability report it full.
        // If any service has WL open, treat the day as WL-available.
        if (dayInfo.waitingListFull !== '1') {
          mergedDay.isWaitingListFull = false;
        }
      }
    }
  }

  // Convert to array and sort by date
  return Array.from(dateMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
}

/**
 * Check if a service is valid (exists in restaurant's service types)
 */
function filterValidServiceTypes(
  serviceTypes: string[],
  validServiceTypes: ServiceType[] = ['breakfast', 'lunch', 'dinner', 'brunch']
): ServiceType[] {
  return serviceTypes.filter((st): st is ServiceType =>
    validServiceTypes.includes(st as ServiceType)
  );
}

/**
 * Adjust calendar for preferred access users.
 * Days that have ONLY preferred availability (hasPreferredAvailability=true, hasAnyAvailability=false)
 * are promoted to "available" so preferred users can see and select them.
 */
function adjustCalendarForPreferredAccess(
  calendar: MergedCalendarDay[],
  hasPreferredAccess: boolean,
): MergedCalendarDay[] {
  if (!hasPreferredAccess) return calendar;

  return calendar.map((day) => {
    if (!day.hasAnyAvailability && day.hasPreferredAvailability) {
      return {
        ...day,
        hasAnyAvailability: true,
        displayType: 'available' as AvailabilityDisplayType,
      };
    }
    return day;
  });
}

interface UseCalendarAvailabilityOptions {
  /** Restaurant ID */
  restaurantId: number | undefined;
  /** Party size (number of diners) */
  partySize: number;
  /** Service types to fetch (from restaurant.serviceTypes) */
  serviceTypes: string[];
  /** Whether to enable the queries */
  enabled?: boolean;
  /** Meitre Network marketplace menu ID — when set, uses /calendar-marketplace endpoint */
  meitreNetworkMenuId?: number | null;
  /** Menu ID pre-selected from landing page — uses /calendar-marketplace for date filtering */
  preSelectedMenuId?: number | null;
  /** Whether the current user has preferred access (extends visible availability) */
  hasPreferredAccess?: boolean;
}

interface UseCalendarAvailabilityResult {
  /** Raw calendar data per service */
  calendarByService: ServiceCalendarAvailability[];
  /** Merged calendar with all services */
  mergedCalendar: MergedCalendarDay[];
  /** Whether any query is loading */
  isLoading: boolean;
  /** Whether all queries have finished (success or error) */
  isSettled: boolean;
  /** Combined error from any failed query */
  error: Error | null;
  /** Refetch all calendars */
  refetch: () => void;
  /** Get availability for a specific date */
  getDateAvailability: (date: string) => MergedCalendarDay | undefined;
  /** Get available service types for a specific date */
  getAvailableServicesForDate: (date: string) => ServiceType[];
  /** Check if a specific date has any availability */
  hasAvailability: (date: string) => boolean;
  /** Prefetch calendar for a different party size */
  prefetchForPartySize: (newPartySize: number) => void;
}

/**
 * Hook to fetch and manage calendar availability for all service types
 *
 * @example
 * ```tsx
 * const { mergedCalendar, isLoading, getAvailableServicesForDate } = useCalendarAvailability({
 *   restaurantId: restaurant.id,
 *   partySize: diners,
 *   serviceTypes: restaurant.serviceTypes,
 *   enabled: diners > 0,
 * });
 *
 * // Get services available for a specific date
 * const availableServices = getAvailableServicesForDate('2025-01-15');
 * ```
 */
export function useCalendarAvailability({
  restaurantId,
  partySize,
  serviceTypes,
  enabled = true,
  meitreNetworkMenuId,
  preSelectedMenuId,
  hasPreferredAccess = false,
}: UseCalendarAvailabilityOptions): UseCalendarAvailabilityResult {
  const queryClient = useQueryClient();

  // Use marketplace calendar endpoint when either marketplace or pre-selected menu is set
  const effectiveMenuId = meitreNetworkMenuId || preSelectedMenuId || null;

  // Filter to only valid service types
  const validServiceTypes = useMemo(
    () => filterValidServiceTypes(serviceTypes),
    [serviceTypes]
  );

  // Create parallel queries for each service type
  const queries = useQueries({
    queries: validServiceTypes.map((serviceType) => ({
      queryKey: calendarAvailabilityKeys.byService(
        restaurantId!,
        partySize,
        serviceType,
        effectiveMenuId,
      ),
      queryFn: () =>
        fetchCalendarAvailability(restaurantId!, partySize, serviceType, effectiveMenuId),
      enabled: enabled && !!restaurantId && partySize > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    })),
  });

  // Aggregate results
  const calendarByService = useMemo(() => {
    return queries
      .filter((q) => q.isSuccess && q.data)
      .map((q) => q.data as ServiceCalendarAvailability);
  }, [queries]);

  // Merge calendars and adjust for preferred access
  const mergedCalendar = useMemo(
    () => adjustCalendarForPreferredAccess(
      mergeCalendarAvailability(calendarByService),
      hasPreferredAccess,
    ),
    [calendarByService, hasPreferredAccess]
  );

  // Create a map for quick date lookup
  const dateMap = useMemo(() => {
    const map = new Map<string, MergedCalendarDay>();
    for (const day of mergedCalendar) {
      map.set(day.date, day);
    }
    return map;
  }, [mergedCalendar]);

  // Derived loading state
  const isLoading = queries.some((q) => q.isLoading);
  const isSettled = queries.every((q) => q.isSuccess || q.isError);

  // Get first error if any
  const error = useMemo(() => {
    const failedQuery = queries.find((q) => q.isError);
    return failedQuery?.error as Error | null;
  }, [queries]);

  // Refetch all calendars
  const refetch = useCallback(() => {
    queries.forEach((q) => q.refetch());
  }, [queries]);

  // Helper: Get availability for a specific date
  const getDateAvailability = useCallback(
    (date: string): MergedCalendarDay | undefined => {
      return dateMap.get(date);
    },
    [dateMap]
  );

  // Helper: Get available services for a date
  const getAvailableServicesForDate = useCallback(
    (date: string): ServiceType[] => {
      const day = dateMap.get(date);
      return day?.availableServiceTypes ?? [];
    },
    [dateMap]
  );

  // Helper: Check if date has any availability
  const hasAvailability = useCallback(
    (date: string): boolean => {
      const day = dateMap.get(date);
      return day?.hasAnyAvailability ?? false;
    },
    [dateMap]
  );

  // Prefetch for different party size
  const prefetchForPartySize = useCallback(
    (newPartySize: number) => {
      if (!restaurantId || newPartySize <= 0) return;

      validServiceTypes.forEach((serviceType) => {
        queryClient.prefetchQuery({
          queryKey: calendarAvailabilityKeys.byService(
            restaurantId,
            newPartySize,
            serviceType,
            effectiveMenuId,
          ),
          queryFn: () =>
            fetchCalendarAvailability(restaurantId, newPartySize, serviceType, effectiveMenuId),
          staleTime: 5 * 60 * 1000,
        });
      });
    },
    [restaurantId, validServiceTypes, queryClient, effectiveMenuId]
  );

  return {
    calendarByService,
    mergedCalendar,
    isLoading,
    isSettled,
    error,
    refetch,
    getDateAvailability,
    getAvailableServicesForDate,
    hasAvailability,
    prefetchForPartySize,
  };
}

/**
 * Get calendar day display type for styling purposes.
 * Returns the displayType directly from the merged day, or calculates it for backward compatibility.
 */
export function getCalendarDayStatus(
  day: MergedCalendarDay | undefined
): AvailabilityDisplayType {
  if (!day) return 'closed';

  // Use the pre-calculated displayType if available
  if (day.displayType) return day.displayType;

  // Fallback: calculate from type
  return mapToDisplayType(day.type);
}

/**
 * Check if a day is clickable (can be selected)
 */
export function isDayClickable(day: MergedCalendarDay | undefined): boolean {
  if (!day) return false;

  const clickableTypes: AvailabilityDisplayType[] = [
    'available',
    'request',
    'waiting-list',
    'walk-in',
  ];

  return clickableTypes.includes(day.displayType);
}
